import { gql, useLazyQuery, useMutation } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { cx } from '@orn/shared-web/utils/cx';
import formatInstantInTimeZone, { PACIFIC_TIME_ZONE } from '@orn/shared-web/utils/formatInstantInTimeZone';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Input from '../../components/Input';
import JsonView from '../../components/JsonView';
import { useToast } from '../../components/Toast';
import { assistantFeedbackAnchorPath } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';

import { firstLineOfSummary } from './compactionSummaryPreview';
import { prefixReason } from './prefixReason';

import type {
  AdminDeleteAllAssistantSessionsForUserMutation,
  AdminDeleteAllAssistantSessionsForUserMutationVariables,
  AdminDeleteAssistantSessionMutation,
  AdminDeleteAssistantSessionMutationVariables,
  AdminGetAssistantSessionDetailQuery,
  AdminGetAssistantSessionDetailQueryVariables,
  AdminListAssistantFeedbackForThreadQuery,
  AdminListAssistantFeedbackForThreadQueryVariables,
  AdminListAssistantSessionsForUserQuery,
  AdminListAssistantSessionsForUserQueryVariables,
  AdminRecompactAssistantSessionMutation,
  AdminRecompactAssistantSessionMutationVariables,
  AdminReExtractAssistantSessionMemoryMutation,
  AdminReExtractAssistantSessionMemoryMutationVariables,
} from './__generated__/AssistantSessionsScene.gql';

const ADMIN_LIST_SESSIONS = gql`
  query AdminListAssistantSessionsForUser($userId: ID!) {
    adminListAssistantSessionsForUser(userId: $userId) {
      hasMore
      sessions {
        id
        userId
        familyId
        channel
        externalThreadKey
        status
        priorThreadId
        lastActivityAt
        createdAt
        updatedAt
        messageCount
      }
    }
  }
`;

const ADMIN_GET_SESSION_DETAIL = gql`
  query AdminGetAssistantSessionDetail($threadId: ID!, $afterSequence: Int) {
    adminGetAssistantSessionDetail(threadId: $threadId, afterSequence: $afterSequence) {
      hasMore
      nextAfterSequence
      session {
        id
        userId
        familyId
        channel
        externalThreadKey
        status
        priorThreadId
        lastActivityAt
        createdAt
        updatedAt
        messageCount
        summary
        compactedAt
        compactionModelId
        compactionMetadata {
          keptCount
          summarizedCount
          droppedCount
          inputTokens
          outputTokens
          errors
        }
        memoryExtractedAt
        memoryExtractionModelId
        memoryExtractionMetadata {
          extractedCount
          userCount
          familyCount
          prefCount
          todoCount
          inputTokens
          outputTokens
          errors
        }
      }
      messages {
        id
        threadId
        turnId
        sequence
        role
        payload
        modelId
        errorMessage
        startedAt
        completedAt
        latencyMs
        thinkingMs
        firstTextAt
        ttftMs
        createdAt
      }
    }
  }
`;

const ADMIN_DELETE_SESSION = gql`
  mutation AdminDeleteAssistantSession($threadId: ID!, $reason: String!) {
    adminDeleteAssistantSession(threadId: $threadId, reason: $reason)
  }
`;

const ADMIN_DELETE_ALL_SESSIONS = gql`
  mutation AdminDeleteAllAssistantSessionsForUser($userId: ID!, $reason: String!) {
    adminDeleteAllAssistantSessionsForUser(userId: $userId, reason: $reason)
  }
`;

const ADMIN_RECOMPACT_SESSION = gql`
  mutation AdminRecompactAssistantSession($threadId: ID!) {
    adminRecompactAssistantSession(threadId: $threadId)
  }
`;

const ADMIN_REEXTRACT_SESSION = gql`
  mutation AdminReExtractAssistantSessionMemory($threadId: ID!) {
    adminReExtractAssistantSessionMemory(threadId: $threadId)
  }
`;

const ADMIN_LIST_FEEDBACK_FOR_THREAD = gql`
  query AdminListAssistantFeedbackForThread($threadId: ID!) {
    adminListAssistantFeedbackForThread(threadId: $threadId) {
      id
      userId
      threadId
      turnId
      body
      submitterEmail
      createdAt
    }
  }
`;

type SessionRow = AdminListAssistantSessionsForUserQuery['adminListAssistantSessionsForUser']['sessions'][number];
type MessageRow = AdminGetAssistantSessionDetailQuery['adminGetAssistantSessionDetail']['messages'][number];
type FeedbackRow = AdminListAssistantFeedbackForThreadQuery['adminListAssistantFeedbackForThread'][number];

// Stable empty reference so turns without feedback don't get a fresh array each
// render (keeps TurnGroup's props referentially stable).
const EMPTY_FEEDBACK: FeedbackRow[] = [];

const CHANNEL_BADGE: Record<string, string> = {
  app: 'border-border-primary text-fg-secondary',
  sms: 'border-border-brand text-fg-brand-primary',
  email: 'border-fg-warning-primary text-fg-warning-primary',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'border-border-primary text-fg-primary',
  archived: 'border-border-primary text-fg-tertiary',
};

interface IRoleAccent {
  border: string;
  label: string;
  /**
   * Color for the row's `headerPreview` slot — the inline body excerpt
   * (user/assistant prose, tool-call params, system kind). Three tiers
   * by information value: user + assistant get `fg-primary` (brightest —
   * what the operator is actually trying to read); toolResult /
   * system_* get `fg-tertiary` (dim — supporting metadata). The role
   * label itself keeps its accent color regardless, providing
   * row-type recognition independent of preview brightness.
   */
  prose: string;
}

const ROLE_ACCENT: Record<string, IRoleAccent> = {
  user: { border: 'border-fg-brand-primary', label: 'text-fg-brand-primary', prose: 'text-fg-primary' },
  assistant: { border: 'border-fg-success-primary', label: 'text-fg-success-primary', prose: 'text-fg-primary' },
  toolResult: { border: 'border-fg-warning-primary', label: 'text-fg-warning-primary', prose: 'text-fg-tertiary' },
  system_instruction: { border: 'border-border-primary', label: 'text-fg-tertiary', prose: 'text-fg-tertiary' },
  system_observation: { border: 'border-border-primary', label: 'text-fg-tertiary', prose: 'text-fg-tertiary' },
};

const DEFAULT_ROLE_ACCENT: IRoleAccent = {
  border: 'border-border-primary',
  label: 'text-fg-secondary',
  prose: 'text-fg-tertiary',
};

/**
 * Sessions sub-tab of the Assistant tool. Operator picks a user; the
 * pane loads every thread (active + archived) for that user, ordered by
 * last-activity desc. Click a thread on the left to see its messages on
 * the right — each rendered as a JSON-style card colored by role, with
 * user / assistant prose bolded and brought to the front. Per-row delete
 * + bulk-delete-all-sessions are gated behind typed-reason modals;
 * deletes are hard via FK cascade (no soft-delete column).
 */
const AssistantSessionsScene = () => {
  // Deep-link params (set when arriving from the Feedback tab). Read once for
  // the initial state; `highlightTurnId` is also read every render to badge +
  // scroll the targeted turn.
  const [searchParams] = useSearchParams();
  const linkUserId = searchParams.get('userId');
  const linkFamilyId = searchParams.get('familyId');
  const linkThreadId = searchParams.get('threadId');
  const highlightTurnId = searchParams.get('turnId');

  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(
    linkUserId && linkFamilyId ? { userId: linkUserId, familyId: linkFamilyId } : null
  );
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(linkThreadId);
  const [deleteThreadConfirm, setDeleteThreadConfirm] = React.useState<SessionRow | null>(null);
  const [deleteReason, setDeleteReason] = React.useState('');
  const [deleteAllOpen, setDeleteAllOpen] = React.useState(false);
  const [deleteAllReason, setDeleteAllReason] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  // Thread whose re-compaction is in flight. Latched on click so the link
  // becomes a non-interactive "processing…" label (prevents double-fire).
  // Backend is fire-and-forget with no completion signal, so it stays latched
  // until the operator switches threads (cleared in the detail-load effect) or
  // refreshes — the toast tells them to refresh to see the result.
  const [recompactingThreadId, setRecompactingThreadId] = React.useState<string | null>(null);
  // Thread whose memory re-extraction is in flight — same latch contract as
  // `recompactingThreadId`.
  const [reextractingThreadId, setReextractingThreadId] = React.useState<string | null>(null);
  // Latches once we auto-pick the first session for a given user. Reset
  // on user switch so the next user gets their own auto-pick, but
  // unchanged on delete-then-refetch — operators who just confirmed a
  // delete shouldn't have a sibling session jump into the detail pane
  // uninvited.
  // Skip the first-session auto-pick when we arrived with a deep-linked thread —
  // the operator wants that exact thread, not sessions[0].
  const hasAutoSelectedRef = React.useRef(linkThreadId !== null);

  const { showToast } = useToast();
  const { user: mopsUser } = useAuthState();

  const userId = selection?.userId ?? null;

  const [loadSessions, { data: sessionsData, loading: sessionsLoading, refetch: refetchSessions }] = useLazyQuery<
    AdminListAssistantSessionsForUserQuery,
    AdminListAssistantSessionsForUserQueryVariables
  >(ADMIN_LIST_SESSIONS, { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

  const [loadSessionDetail, { data: detailData, loading: detailLoading, fetchMore: fetchMoreMessages }] = useLazyQuery<
    AdminGetAssistantSessionDetailQuery,
    AdminGetAssistantSessionDetailQueryVariables
  >(ADMIN_GET_SESSION_DETAIL, { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

  const [loadFeedback, { data: feedbackData }] = useLazyQuery<
    AdminListAssistantFeedbackForThreadQuery,
    AdminListAssistantFeedbackForThreadQueryVariables
  >(ADMIN_LIST_FEEDBACK_FOR_THREAD, { fetchPolicy: 'cache-and-network' });

  const [deleteSession] = useMutation<
    AdminDeleteAssistantSessionMutation,
    AdminDeleteAssistantSessionMutationVariables
  >(ADMIN_DELETE_SESSION);

  const [deleteAllSessions] = useMutation<
    AdminDeleteAllAssistantSessionsForUserMutation,
    AdminDeleteAllAssistantSessionsForUserMutationVariables
  >(ADMIN_DELETE_ALL_SESSIONS);

  const [recompactSession] = useMutation<
    AdminRecompactAssistantSessionMutation,
    AdminRecompactAssistantSessionMutationVariables
  >(ADMIN_RECOMPACT_SESSION);

  const [reextractSession] = useMutation<
    AdminReExtractAssistantSessionMemoryMutation,
    AdminReExtractAssistantSessionMemoryMutationVariables
  >(ADMIN_REEXTRACT_SESSION);

  React.useEffect(() => {
    if (userId) {
      void loadSessions({ variables: { userId } });
    }
  }, [userId, loadSessions]);

  React.useEffect(() => {
    setRecompactingThreadId(null);
    setReextractingThreadId(null);
    if (selectedThreadId) {
      void loadSessionDetail({ variables: { threadId: selectedThreadId, afterSequence: null } });
      void loadFeedback({ variables: { threadId: selectedThreadId } });
    }
  }, [selectedThreadId, loadSessionDetail, loadFeedback]);

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setSelectedThreadId(null);
    setDeleteThreadConfirm(null);
    setDeleteReason('');
    setDeleteAllOpen(false);
    setDeleteAllReason('');
    hasAutoSelectedRef.current = false;
  };

  const sessions = React.useMemo(() => sessionsData?.adminListAssistantSessionsForUser?.sessions ?? [], [sessionsData]);
  const sessionsHasMore = sessionsData?.adminListAssistantSessionsForUser?.hasMore ?? false;
  const detail = detailData?.adminGetAssistantSessionDetail ?? null;

  // Feedback for the selected thread, grouped by the turn it targets. Filtered
  // to the active thread so a stale response from a just-switched thread can't
  // bleed badges onto the wrong turns.
  const feedbackByTurnId = React.useMemo(() => {
    const map = new Map<string, FeedbackRow[]>();
    for (const fb of feedbackData?.adminListAssistantFeedbackForThread ?? []) {
      if (fb.threadId !== selectedThreadId) {
        continue;
      }
      const list = map.get(fb.turnId) ?? [];
      list.push(fb);
      map.set(fb.turnId, list);
    }
    return map;
  }, [feedbackData, selectedThreadId]);

  // Auto-pick the first session once per user selection so the operator
  // lands on real data instead of a blank pane. The ref guard prevents
  // a post-delete refetch from quietly jumping the operator to a sibling
  // session — `selectedThreadId === null` after a delete looks identical
  // to "nothing loaded yet" without it.
  React.useEffect(() => {
    if (!hasAutoSelectedRef.current && selectedThreadId === null && sessions.length > 0) {
      hasAutoSelectedRef.current = true;
      setSelectedThreadId(sessions[0].id);
    }
  }, [sessions, selectedThreadId]);

  // Resolver paginates forward in `sequence ASC` (the orchestrator's
  // reconnect-replay shape); each call adds messages that came after the
  // ones we already have, so the affordance reads "Load more" rather
  // than "older" / "newer" to stay direction-honest. Whether the pane
  // should flip to chat-style "newest first" UX is tracked separately.
  const handleLoadMoreMessages = async () => {
    if (!detail || !detail.hasMore || detail.nextAfterSequence === null || detail.nextAfterSequence === undefined) {
      return;
    }
    await fetchMoreMessages({
      variables: { threadId: detail.session.id, afterSequence: detail.nextAfterSequence },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          adminGetAssistantSessionDetail: {
            ...fetchMoreResult.adminGetAssistantSessionDetail,
            messages: [
              ...prev.adminGetAssistantSessionDetail.messages,
              ...fetchMoreResult.adminGetAssistantSessionDetail.messages,
            ],
          },
        };
      },
    });
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteThreadConfirm || deleteReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      await deleteSession({
        variables: {
          threadId: deleteThreadConfirm.id,
          reason: prefixReason(deleteReason, mopsUser?.email ?? null),
        },
      });
      showToast({ type: 'success', message: 'Session deleted.' });
      const wasSelected = selectedThreadId === deleteThreadConfirm.id;
      setDeleteThreadConfirm(null);
      setDeleteReason('');
      if (wasSelected) {
        setSelectedThreadId(null);
      }
      await refetchSessions();
    } catch {
      // Apollo errorLink reports to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  // Force a fresh compaction run on an archived thread so we can eyeball the
  // result on real data. Fire-and-forget on the backend (the `@orn/api` worker
  // returns 202 and runs the LLM call in the background), so there's no
  // deterministic completion signal — the toast tells the operator to refresh.
  const handleRecompactSession = async (threadId: string) => {
    setRecompactingThreadId(threadId);
    try {
      await recompactSession({ variables: { threadId } });
      showToast({
        type: 'info',
        message: 'Re-compaction queued — it will finish shortly. Refresh in a few seconds to see the result.',
      });
      // Stays latched as "processing…" — there's no completion signal for the
      // background compaction; switching threads or refreshing clears it.
    } catch {
      // Apollo errorLink reports to Sentry; a 404/409 from the worker surfaces
      // as a GraphQL error here. Unlatch so the operator can retry.
      setRecompactingThreadId(null);
    }
  };

  // Re-run memory extraction on an archived thread: the worker deletes the
  // thread's prior extractor memories and re-mines them. Same fire-and-forget
  // latch contract as re-compaction. A 409 (non-archived, or memory disabled
  // for the user) surfaces as a GraphQL error → caught here, unlatched.
  const handleReextractSession = async (threadId: string) => {
    setReextractingThreadId(threadId);
    try {
      await reextractSession({ variables: { threadId } });
      showToast({
        type: 'info',
        message: 'Re-extraction queued — it will finish shortly. Refresh in a few seconds to see the result.',
      });
    } catch {
      setReextractingThreadId(null);
    }
  };

  const handleConfirmDeleteAll = async () => {
    if (!userId || deleteAllReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      const result = await deleteAllSessions({
        variables: { userId, reason: prefixReason(deleteAllReason, mopsUser?.email ?? null) },
      });
      const count = result.data?.adminDeleteAllAssistantSessionsForUser ?? 0;
      showToast({
        type: 'success',
        message: count === 0 ? 'No sessions to delete.' : `Deleted ${count} sessions.`,
      });
      setDeleteAllOpen(false);
      setDeleteAllReason('');
      setSelectedThreadId(null);
      await refetchSessions();
    } catch {
      // Apollo errorLink reports to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="w-full max-w-md">
          <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
        </div>
        {userId && sessions.length > 0 && (
          <Button color="danger" size="sm" isDisabled={actionLoading} onClick={() => setDeleteAllOpen(true)}>
            Delete all sessions
          </Button>
        )}
      </div>

      {!userId ? (
        <div className="text-fg-tertiary text-xs italic">Select a user to view their assistant sessions.</div>
      ) : sessionsLoading && sessions.length === 0 ? (
        <div className="text-fg-secondary text-xs">Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No assistant sessions for this user.</div>
      ) : (
        <div className="border-border-primary flex min-h-0 flex-1 border">
          <SessionList
            sessions={sessions}
            selectedThreadId={selectedThreadId}
            onSelect={setSelectedThreadId}
            hasMore={sessionsHasMore}
          />
          <div className="flex-1 overflow-y-auto">
            {selectedThreadId === null ? (
              <div className="text-fg-tertiary p-6 text-xs italic">Pick a session from the left to inspect it.</div>
            ) : detailLoading && detail === null ? (
              <div className="text-fg-secondary p-6 text-xs">Loading messages…</div>
            ) : detail === null ? (
              <div className="text-fg-tertiary p-6 text-xs italic">Session detail unavailable.</div>
            ) : (
              <SessionDetail
                detail={detail}
                actionLoading={actionLoading}
                onDelete={() => {
                  setDeleteThreadConfirm(detail.session);
                  setDeleteReason('');
                }}
                onRecompact={() => void handleRecompactSession(detail.session.id)}
                isRecompacting={recompactingThreadId === detail.session.id}
                onReextract={() => void handleReextractSession(detail.session.id)}
                isReextracting={reextractingThreadId === detail.session.id}
                onLoadMore={() => void handleLoadMoreMessages()}
                onJumpToPrior={(priorId) => {
                  if (sessions.some((s) => s.id === priorId)) {
                    setSelectedThreadId(priorId);
                  }
                }}
                sessionsInList={sessions}
                feedbackByTurnId={feedbackByTurnId}
                highlightTurnId={highlightTurnId}
              />
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={deleteThreadConfirm !== null}
        onOpenChange={(open) => !open && setDeleteThreadConfirm(null)}
        size="md"
      >
        <Modal.Header onClose={() => setDeleteThreadConfirm(null)}>Delete session</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              Hard-deletes this thread and all its messages — irreversible. If the user has an active WebSocket
              conversation in this thread right now, the in-flight turn may be lost. Reason is recorded in operational
              logs.
            </div>
            {deleteThreadConfirm && (
              <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
                <div className="font-mono text-[11px]">
                  {deleteThreadConfirm.id} · {deleteThreadConfirm.messageCount} msg · {deleteThreadConfirm.channel} ·{' '}
                  {deleteThreadConfirm.status}
                </div>
              </div>
            )}
            <Input
              label="Reason"
              placeholder="e.g. user privacy request, support ticket #1234"
              value={deleteReason}
              onChange={setDeleteReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setDeleteThreadConfirm(null)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={deleteReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleConfirmDeleteSession()}
              >
                Delete session
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal isOpen={deleteAllOpen} onOpenChange={(open) => !open && setDeleteAllOpen(false)} size="md">
        <Modal.Header onClose={() => setDeleteAllOpen(false)}>Delete all sessions for user</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              Hard-deletes <span className="font-semibold">every</span> assistant thread (active and archived) and all
              messages for this user — irreversible. If the user has an active WebSocket conversation, the in-flight
              turn may be lost. Reason is recorded in operational logs.
            </div>
            <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
              <span className="font-mono text-[11px]">
                {sessions.length} session{sessions.length === 1 ? '' : 's'} on record
              </span>
            </div>
            <Input
              label="Reason"
              placeholder="e.g. account deletion / GDPR request"
              value={deleteAllReason}
              onChange={setDeleteAllReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setDeleteAllOpen(false)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={deleteAllReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleConfirmDeleteAll()}
              >
                Delete all
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

interface ISessionListProps {
  sessions: SessionRow[];
  selectedThreadId: string | null;
  onSelect: (threadId: string) => void;
  hasMore: boolean;
}

const SessionList = ({ sessions, selectedThreadId, onSelect, hasMore }: ISessionListProps) => (
  <aside className="border-border-primary w-72 shrink-0 overflow-y-auto border-r">
    {hasMore && (
      <div className="border-border-primary text-fg-warning-primary border-b px-3 py-2 text-[11px]">
        Showing 100 most recent · older sessions hidden
      </div>
    )}
    <ul>
      {sessions.map((session) => {
        const isSelected = session.id === selectedThreadId;
        return (
          <li key={session.id}>
            <button
              type="button"
              onClick={() => onSelect(session.id)}
              className={cx(
                'border-border-primary flex w-full flex-col gap-1 border-b px-3 py-2 text-left transition-colors',
                isSelected
                  ? 'bg-bg-secondary text-fg-primary border-l-fg-brand-primary border-l-2 pl-[10px]'
                  : 'hover:bg-bg-secondary text-fg-secondary'
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium">{formatTimeAgo(session.lastActivityAt)}</span>
                <span className="text-fg-quaternary font-mono text-[10px]">{session.id.slice(-8)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={cx(
                    'inline-flex items-center border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider',
                    CHANNEL_BADGE[session.channel] ?? CHANNEL_BADGE.app
                  )}
                >
                  {session.channel}
                </span>
                <span
                  className={cx(
                    'inline-flex items-center border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider',
                    STATUS_BADGE[session.status] ?? STATUS_BADGE.active
                  )}
                >
                  {session.status}
                </span>
                <span className="text-fg-tertiary font-mono text-[10px]">{session.messageCount} msg</span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  </aside>
);

type SessionDetailRow = NonNullable<AdminGetAssistantSessionDetailQuery['adminGetAssistantSessionDetail']>['session'];

interface ICompactionHeaderRowsProps {
  session: SessionDetailRow;
}

/**
 * Compaction stats as `<dl>` rows for the thread header. Renders inside
 * the parent's grid (so it must emit bare `<dt>`/`<dd>` pairs, not a
 * wrapper). Shows the per-run counts + token spend once `compactedAt` is
 * set; a single "not compacted" row until then.
 */
const CompactionHeaderRows = ({ session }: ICompactionHeaderRowsProps) => {
  if (session.compactedAt === null || session.compactedAt === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">compaction</dt>
        <dd className="text-fg-tertiary italic">not compacted</dd>
      </>
    );
  }
  const m = session.compactionMetadata;
  return (
    <>
      <dt className="text-fg-tertiary">compacted</dt>
      <dd className="font-mono">{formatTimeAgo(session.compactedAt)}</dd>
      <dt className="text-fg-tertiary pl-3">model</dt>
      <dd className="font-mono">{session.compactionModelId ?? '—'}</dd>
      {m !== null && m !== undefined && (
        <>
          <dt className="text-fg-tertiary pl-3" title="kept / summarized / dropped messages">
            kept/summ/drop
          </dt>
          <dd className="font-mono tabular-nums">
            {m.keptCount} / {m.summarizedCount} / {m.droppedCount}
          </dd>
          <dt className="text-fg-tertiary pl-3">tokens</dt>
          <dd className="font-mono tabular-nums">
            {m.inputTokens} in / {m.outputTokens} out
          </dd>
          {m.errors !== null && m.errors !== undefined && m.errors.length > 0 && (
            <>
              <dt className="text-fg-tertiary pl-3">errors</dt>
              <dd className="text-fg-error-primary font-mono tabular-nums" title={m.errors.join('\n')}>
                {m.errors.length}
              </dd>
            </>
          )}
        </>
      )}
    </>
  );
};

/**
 * Memory-extraction stats as `<dl>` rows for the thread header — the backup
 * pass that mines durable memories the agent's in-session tool missed. Same
 * bare-`<dt>`/`<dd>` contract as {@link CompactionHeaderRows}; a single "not
 * extracted" row until `memoryExtractedAt` is set.
 */
const MemoryExtractionHeaderRows = ({ session }: ICompactionHeaderRowsProps) => {
  if (session.memoryExtractedAt === null || session.memoryExtractedAt === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">extraction</dt>
        <dd className="text-fg-tertiary italic">not extracted</dd>
      </>
    );
  }
  const m = session.memoryExtractionMetadata;
  return (
    <>
      <dt className="text-fg-tertiary">extracted</dt>
      <dd className="font-mono">{formatTimeAgo(session.memoryExtractedAt)}</dd>
      <dt className="text-fg-tertiary pl-3">model</dt>
      <dd className="font-mono">{session.memoryExtractionModelId ?? '—'}</dd>
      {m !== null && m !== undefined && (
        <>
          <dt className="text-fg-tertiary pl-3" title="total mined (user / family · pref / todo)">
            mined
          </dt>
          <dd className="font-mono tabular-nums">
            {m.extractedCount} ({m.userCount} usr / {m.familyCount} fam · {m.prefCount} pref / {m.todoCount} todo)
          </dd>
          <dt className="text-fg-tertiary pl-3">tokens</dt>
          <dd className="font-mono tabular-nums">
            {m.inputTokens} in / {m.outputTokens} out
          </dd>
          {m.errors !== null && m.errors !== undefined && m.errors.length > 0 && (
            <>
              <dt className="text-fg-tertiary pl-3">errors</dt>
              <dd className="text-fg-error-primary font-mono tabular-nums" title={m.errors.join('\n')}>
                {m.errors.length}
              </dd>
            </>
          )}
        </>
      )}
    </>
  );
};

interface ICompactionSummaryPanelProps {
  summary: string;
}

/**
 * The compaction summary, collapsed to a one-line preview that expands to
 * the full narrative on click. Mirrors `CollapsibleMessageCard`'s header
 * affordance (chevron + uppercase label + truncated preview). Only
 * rendered when the thread carries a non-empty summary.
 */
const CompactionSummaryPanel = ({ summary }: ICompactionSummaryPanelProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const preview = firstLineOfSummary(summary);
  return (
    <div className="border-border-primary border-b pb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="hover:bg-bg-secondary -ml-1 flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] uppercase"
      >
        <span className="text-fg-quaternary shrink-0">{expanded ? '▾' : '▸'}</span>
        <span className="text-fg-brand-primary shrink-0">summary</span>
        {!expanded && <span className="text-fg-secondary min-w-0 flex-1 truncate normal-case">{preview}</span>}
      </button>
      {expanded && (
        <div className="text-fg-primary whitespace-pre-wrap px-1 pt-2 font-mono text-[11px] leading-snug">
          {summary}
        </div>
      )}
    </div>
  );
};

interface ISessionDetailProps {
  detail: NonNullable<AdminGetAssistantSessionDetailQuery['adminGetAssistantSessionDetail']>;
  actionLoading: boolean;
  onDelete: () => void;
  onRecompact: () => void;
  isRecompacting: boolean;
  onReextract: () => void;
  isReextracting: boolean;
  onLoadMore: () => void;
  onJumpToPrior: (priorThreadId: string) => void;
  sessionsInList: SessionRow[];
  feedbackByTurnId: Map<string, FeedbackRow[]>;
  /** turnId to scroll to + highlight when arriving from a Feedback-tab deep link. */
  highlightTurnId: string | null;
}

const SessionDetail = ({
  detail,
  actionLoading,
  onDelete,
  onRecompact,
  isRecompacting,
  onReextract,
  isReextracting,
  onLoadMore,
  onJumpToPrior,
  sessionsInList,
  feedbackByTurnId,
  highlightTurnId,
}: ISessionDetailProps) => {
  const { session, messages, hasMore } = detail;
  const priorIsInList = session.priorThreadId !== null && sessionsInList.some((s) => s.id === session.priorThreadId);
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="border-border-primary flex items-start justify-between gap-3 border-b pb-3">
        <dl className="text-fg-secondary grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          <dt className="text-fg-tertiary">id</dt>
          <dd className="text-fg-primary font-mono">{session.id}</dd>
          <dt className="text-fg-tertiary">channel</dt>
          <dd>{session.channel}</dd>
          <dt className="text-fg-tertiary">status</dt>
          <dd>{session.status}</dd>
          <dt className="text-fg-tertiary">messages</dt>
          <dd>{session.messageCount}</dd>
          <dt className="text-fg-tertiary">created</dt>
          <dd className="font-mono">{formatInstantInTimeZone(session.createdAt, PACIFIC_TIME_ZONE)}</dd>
          <dt className="text-fg-tertiary">last activity</dt>
          <dd className="font-mono">{formatTimeAgo(session.lastActivityAt)}</dd>
          {session.priorThreadId && (
            <>
              <dt className="text-fg-tertiary">prior</dt>
              <dd className="font-mono">
                {priorIsInList ? (
                  <button
                    type="button"
                    onClick={() => session.priorThreadId && onJumpToPrior(session.priorThreadId)}
                    className="text-fg-brand-primary hover:underline"
                  >
                    {session.priorThreadId.slice(-8)}
                  </button>
                ) : (
                  <span className="text-fg-tertiary" title={session.priorThreadId}>
                    {session.priorThreadId.slice(-8)} (not loaded)
                  </span>
                )}
              </dd>
            </>
          )}
          <CompactionHeaderRows session={session} />
          <dt className="text-fg-tertiary pl-3">re-compact</dt>
          <dd>
            {isRecompacting ? (
              <span className="text-fg-quaternary italic">processing…</span>
            ) : (
              <button
                type="button"
                onClick={onRecompact}
                disabled={actionLoading || session.status !== 'archived'}
                title={session.status === 'archived' ? undefined : 'Only archived sessions can be re-compacted'}
                className="text-fg-brand-primary disabled:text-fg-quaternary hover:underline disabled:cursor-not-allowed disabled:no-underline"
              >
                re-compact now
              </button>
            )}
          </dd>
          <MemoryExtractionHeaderRows session={session} />
          <dt className="text-fg-tertiary pl-3">re-extract</dt>
          <dd>
            {isReextracting ? (
              <span className="text-fg-quaternary italic">processing…</span>
            ) : (
              <button
                type="button"
                onClick={onReextract}
                disabled={actionLoading || session.status !== 'archived'}
                title={session.status === 'archived' ? undefined : 'Only archived sessions can be re-extracted'}
                className="text-fg-brand-primary disabled:text-fg-quaternary hover:underline disabled:cursor-not-allowed disabled:no-underline"
              >
                re-extract now
              </button>
            )}
          </dd>
        </dl>
        <Button color="danger" size="sm" isDisabled={actionLoading} onClick={onDelete}>
          Delete session
        </Button>
      </div>

      {session.summary !== null && session.summary !== undefined && session.summary.length > 0 && (
        <CompactionSummaryPanel summary={session.summary} />
      )}

      {messages.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No messages persisted for this thread.</div>
      ) : (
        <div className="flex flex-col">
          {groupMessagesByTurn(messages).map((group) => (
            <TurnGroup
              key={group.turnId}
              group={group}
              feedback={feedbackByTurnId.get(group.turnId) ?? EMPTY_FEEDBACK}
              highlight={highlightTurnId === group.turnId}
            />
          ))}
          {hasMore && (
            <div className="flex justify-center py-3">
              <Button color="secondary" size="sm" isDisabled={actionLoading} onClick={onLoadMore}>
                Load more messages
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ITurnGroup {
  turnId: string;
  messages: MessageRow[];
  /** ms since epoch of the earliest `startedAt` in the group, or null if every row lacks timing. */
  startedAtMs: number | null;
  /** ms since epoch of the latest `completedAt` in the group, or null if every row lacks timing. */
  completedAtMs: number | null;
  durationMs: number | null;
  roundCount: number;
  toolCallCount: number;
}

/**
 * Partition the loaded messages into ordered turn groups. Each turn's
 * rows are already contiguous in `sequence ASC` (the controller's append
 * contract holds them together), so a single linear pass suffices —
 * boundaries are wherever the `turnId` changes from the previous row.
 *
 * Aggregates (`startedAtMs` / `completedAtMs` / `roundCount` /
 * `toolCallCount`) are computed in the same pass so the header can
 * render them without re-walking the group. Rows that pre-date the
 * timing columns (`startedAt`/`completedAt` NULL) contribute to the
 * row count but not to the duration extent.
 */
const groupMessagesByTurn = (messages: readonly MessageRow[]): ITurnGroup[] => {
  const groups: ITurnGroup[] = [];
  let current: ITurnGroup | null = null;
  for (const m of messages) {
    if (current === null || current.turnId !== m.turnId) {
      current = {
        turnId: m.turnId,
        messages: [],
        startedAtMs: null,
        completedAtMs: null,
        durationMs: null,
        roundCount: 0,
        toolCallCount: 0,
      };
      groups.push(current);
    }
    current.messages.push(m);
    if (m.role === 'assistant') {
      current.roundCount += 1;
    }
    if (m.role === 'toolResult') {
      current.toolCallCount += 1;
    }
    if (m.startedAt) {
      const ms = new Date(m.startedAt).getTime();
      if (current.startedAtMs === null || ms < current.startedAtMs) {
        current.startedAtMs = ms;
      }
    }
    if (m.completedAt) {
      const ms = new Date(m.completedAt).getTime();
      if (current.completedAtMs === null || ms > current.completedAtMs) {
        current.completedAtMs = ms;
      }
    }
  }
  for (const g of groups) {
    if (g.startedAtMs !== null && g.completedAtMs !== null) {
      g.durationMs = g.completedAtMs - g.startedAtMs;
    }
  }
  return groups;
};

/**
 * Pretty-print a millisecond duration for the turn header / latency
 * chips. `< 1s` shows as `123ms`; `≥ 1s` shows as `1.23s` so the
 * operator can scan turn lengths at a glance. NULL → `—`. Compact
 * (no space before unit) so per-row chips align cleanly in
 * fixed-width column cells.
 */
const formatDurationMs = (ms: number | null): string => {
  if (ms === null) {
    return '—';
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

interface ITurnGroupProps {
  group: ITurnGroup;
  /** Internal `/issue` feedback targeting this turn (empty when none). */
  feedback: FeedbackRow[];
  /** When true (deep-linked turn), scroll into view + ring the section. */
  highlight: boolean;
}

/**
 * One renderable chunk inside a turn group. Single `MessageCard`s
 * land as `{ kind: 'single' }`. Contiguous `toolResult` runs of 2+
 * land as `{ kind: 'parallel' }` — by the Anthropic API contract,
 * such a run always pairs to ONE assistant message with N tool_use
 * blocks, and pi-agent-core's `toolExecution: 'parallel'` default
 * means those N tools dispatch concurrently. Single-tool rounds
 * (run length 1) stay as `'single'` since there's nothing to bracket.
 */
type TurnSegment =
  | { kind: 'single'; message: MessageRow }
  | {
      kind: 'parallel';
      messages: MessageRow[];
      /** ms since epoch — earliest `startedAt` across the parallel siblings (≈ when the assistant dispatched them). */
      startedAtMs: number | null;
      /** Max of each sibling's `latencyMs` — the slowest tool gates when the next round can start, so this is the wall-clock duration of the parallel block. */
      maxLatencyMs: number | null;
    };

const buildTurnSegments = (messages: readonly MessageRow[]): TurnSegment[] => {
  const segments: TurnSegment[] = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'toolResult') {
      const start = i;
      while (i < messages.length && messages[i].role === 'toolResult') {
        i += 1;
      }
      const slice = messages.slice(start, i);
      if (slice.length >= 2) {
        let startedAtMs: number | null = null;
        let maxLatencyMs: number | null = null;
        for (const m of slice) {
          if (m.startedAt) {
            const ms = new Date(m.startedAt).getTime();
            if (startedAtMs === null || ms < startedAtMs) {
              startedAtMs = ms;
            }
          }
          if (m.latencyMs !== null && m.latencyMs !== undefined) {
            if (maxLatencyMs === null || m.latencyMs > maxLatencyMs) {
              maxLatencyMs = m.latencyMs;
            }
          }
        }
        segments.push({ kind: 'parallel', messages: slice, startedAtMs, maxLatencyMs });
      } else {
        segments.push({ kind: 'single', message: slice[0] });
      }
    } else {
      segments.push({ kind: 'single', message: messages[i] });
      i += 1;
    }
  }
  return segments;
};

/**
 * One label/value cell rendered as a pair of fixed-width sub-cells:
 * label right-aligned in `w-[3ch]`, value right-aligned in `w-[7ch]`.
 * Used in both MessageCard's metadata strip and the parallel-group
 * header — sharing the helper guarantees the columns align across the
 * two surfaces (every chip's value-right-edge lands at the same
 * screen-X regardless of row type). Pass empty strings to render an
 * invisible placeholder cell that still consumes column width — keeps
 * `Δ`/`lat` aligned with their per-row siblings when the chip set
 * differs between contexts.
 */
const renderMetricChip = (label: string, value: string, valueClass = ''): React.ReactNode => (
  // `shrink-0` on the chip wrapper AND on each sub-span prevents flex
  // from collapsing empty-content cells (e.g. the placeholder seq /
  // 🧠 slots on the parallel-group header). Without it, empty cells
  // shrink to zero and the populated chips drift left of their per-row
  // siblings, breaking column alignment.
  <span className="flex shrink-0 items-baseline gap-1.5">
    <span className="text-fg-quaternary w-[3ch] shrink-0 text-right">{label}</span>
    <span className={cx('w-[7ch] shrink-0 text-right', valueClass)}>{value}</span>
  </span>
);

/**
 * Wraps `renderMetricChip`'s output in a per-row metadata container
 * matching MessageCard's strip. Used by the parallel-group header to
 * align its Δ / lat chips with the per-row chips below.
 */
const ParallelMetadata = ({
  startedAtMs,
  maxLatencyMs,
  turnStartedAtMs,
}: {
  startedAtMs: number | null;
  maxLatencyMs: number | null;
  turnStartedAtMs: number | null;
}): React.ReactElement => (
  <span className="text-fg-tertiary flex flex-1 justify-end gap-3 font-mono normal-case tabular-nums">
    {renderMetricChip('', '')}
    <span title="Delta from turn start: earliest startedAt across the parallel siblings (≈ when the assistant dispatched the parallel tool batch).">
      {renderMetricChip('Δ', formatTurnDelta(startedAtMs !== null ? new Date(startedAtMs) : null, turnStartedAtMs))}
    </span>
    {renderMetricChip('', '')}
    {renderMetricChip('', '')}
    <span title="Group latency: max(latencyMs) across the parallel siblings. The slowest tool gates the next round, so this is the wall-clock duration of the parallel block.">
      {renderMetricChip('lat', formatDurationMs(maxLatencyMs), 'text-fg-primary')}
    </span>
  </span>
);

/**
 * Render one turn group: a sticky header summarizing the turn (start
 * timestamp, turnId, message count, round count, tool-call count,
 * duration) followed by the group's rows indented under a left border
 * so the boundary between turns is visible. `MessageCard` receives the
 * group's `startedAtMs` so it can render a Δ-from-turn-start chip.
 *
 * Start-timestamp source: the group's `startedAtMs` (orchestrator-
 * observed turn start). Falls back to the first row's `createdAt` for
 * legacy rows that predate the timing columns — keeps the chip
 * meaningful across the data migration boundary.
 *
 * Parallel tool calls are bracketed: contiguous `toolResult` runs of
 * 2+ get a `⇄ N parallel` header so an operator can tell at a glance
 * that those rows dispatched concurrently rather than serially (the
 * Δ-from-turn-start chips also reveal this on close inspection —
 * parallel siblings share a Δ — but the explicit bracket makes it
 * scannable).
 */
const TurnGroup = ({ group, feedback, highlight }: ITurnGroupProps) => {
  const headerStartedAtMs = group.startedAtMs ?? new Date(group.messages[0].createdAt).getTime();
  const headerTimestamp = formatInstantInTimeZone(new Date(headerStartedAtMs), PACIFIC_TIME_ZONE);
  const segments = buildTurnSegments(group.messages);
  const toolCallArgs = React.useMemo(() => buildToolCallArgsIndex(group.messages), [group.messages]);
  const navigate = useNavigate();
  const sectionRef = React.useRef<HTMLElement>(null);
  // Deep-linked turn: scroll into view and flash the header. Start yellow on
  // first paint (no fade-in), then drop the flag so `transition-colors` fades
  // it back to the normal header bg — a momentary flash rather than a
  // persistent ring (which read as janky against the sticky header).
  const [flashing, setFlashing] = React.useState(highlight);
  React.useEffect(() => {
    if (!highlight || !sectionRef.current) {
      return;
    }
    sectionRef.current.scrollIntoView({ block: 'center' });
    const timer = setTimeout(() => setFlashing(false), 700);
    return () => clearTimeout(timer);
  }, [highlight]);
  return (
    <section ref={sectionRef} className="border-border-primary border-t first:border-t-0">
      <header
        className={cx(
          'text-fg-tertiary sticky top-0 z-10 flex flex-wrap items-center gap-3 px-2 py-1 font-mono text-[11px] uppercase tracking-wide transition-colors duration-700',
          flashing ? 'bg-bg-warning-solid' : 'bg-bg-secondary'
        )}
      >
        <span className="text-fg-primary">{headerTimestamp}</span>
        <span>turn {group.turnId.slice(0, 8)}</span>
        <span>{group.messages.length} msg</span>
        <span>{group.roundCount} rounds</span>
        <span>{group.toolCallCount} tool calls</span>
        <span className="text-fg-primary normal-case">{formatDurationMs(group.durationMs)}</span>
        {feedback.length > 0 && (
          <Tooltip
            wide
            title="Feedback"
            description={
              <div className="flex flex-col gap-2 text-left">
                {feedback.map((fb) => (
                  <div key={fb.id} className="flex flex-col gap-0.5">
                    <div className="font-mono text-[10px] uppercase tracking-wide text-white/80">
                      {fb.submitterEmail ?? fb.userId} · {formatTimeAgo(fb.createdAt)}
                    </div>
                    <div className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">{fb.body}</div>
                  </div>
                ))}
              </div>
            }
          >
            <TooltipTrigger
              onPress={() => void navigate(assistantFeedbackAnchorPath(feedback[0].id))}
              className="border-border-brand text-fg-brand-primary hover:bg-bg-primary cursor-pointer border px-1 py-px font-mono text-[11px] normal-case"
            >
              💬 {feedback.length} feedback
            </TooltipTrigger>
          </Tooltip>
        )}
      </header>
      <div className="border-border-primary ml-1 border-l pl-2">
        {segments.map((segment, idx) => {
          if (segment.kind === 'single') {
            return (
              <MessageCard
                key={segment.message.id}
                message={segment.message}
                turnStartedAtMs={group.startedAtMs}
                toolCallArgs={toolCallArgs}
              />
            );
          }
          return (
            <div key={`parallel-${idx}`} className="border-fg-warning-primary/30 my-1 border-l-2 pl-2">
              {/*
                Right-padding math: per-row chips end at
                `outer-right - 1rem` because each MessageCard's
                article has `pr-2` (0.5rem) AND its inner button has
                `w-full -ml-1 px-1` which adds another 0.5rem of
                effective right inset (button-right = article-content-
                right - 0.25, button-content-right = -0.25 more). The
                parallel header has no article/button wrapper, so it
                must explicitly carry the full 1rem (`pr-4`) on its
                own to land chips in the same screen column as the
                rows below.
              */}
              <div className="flex items-center gap-2 px-1 py-0.5 pr-4 font-mono text-[10px] uppercase tracking-wide">
                <span className="text-fg-quaternary">⇄</span>
                <span className="text-fg-quaternary">{segment.messages.length} parallel</span>
                <ParallelMetadata
                  startedAtMs={segment.startedAtMs}
                  maxLatencyMs={segment.maxLatencyMs}
                  turnStartedAtMs={group.startedAtMs}
                />
              </div>
              {segment.messages.map((message) => (
                <MessageCard
                  key={message.id}
                  message={message}
                  turnStartedAtMs={group.startedAtMs}
                  toolCallArgs={toolCallArgs}
                />
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
};

interface IMessageCardProps {
  message: MessageRow;
  /** ms since epoch of the turn's earliest `startedAt`, for the Δ chip. */
  turnStartedAtMs: number | null;
  /**
   * Map of `toolUseId → input` built once per turn. The toolResult
   * branch reads this to render a one-line param summary next to the
   * tool name. Non-toolResult rows ignore it.
   */
  toolCallArgs: Map<string, unknown>;
}

/**
 * Format the row's `startedAt` as a Δ-from-turn-start offset (e.g.
 * `+1.23s`). The first row of each turn shows `+0.00 s`. Falls back to
 * `—` when either the row or the turn lacks a `startedAt` — typically
 * legacy rows that pre-date the timing columns.
 */
const formatTurnDelta = (rowStartedAt: Date | string | null, turnStartedAtMs: number | null): string => {
  if (rowStartedAt === null || turnStartedAtMs === null) {
    return '—';
  }
  const deltaMs = new Date(rowStartedAt).getTime() - turnStartedAtMs;
  if (deltaMs < 0) {
    return '—';
  }
  if (deltaMs < 1000) {
    return `+${deltaMs}ms`;
  }
  return `+${(deltaMs / 1000).toFixed(2)}s`;
};

/**
 * Walk every assistant row in `messages` and index every `toolCall`
 * content block by its `id`, returning `Map<toolCallId, arguments>` so
 * a paired `toolResult` row's params can be looked up O(1). pi-agent-
 * core normalizes Anthropic's raw `tool_use` (`input`) and OpenAI's
 * `function_call` (`arguments`) into one persisted shape on
 * `AssistantMessage.content[]` — `{ type: 'toolCall', id, name,
 * arguments }` — and pairs the toolResult by `toolCallId` ↔
 * `toolCall.id`. Single pass per turn at render time, no extra fetch.
 */
const buildToolCallArgsIndex = (messages: readonly MessageRow[]): Map<string, unknown> => {
  const index = new Map<string, unknown>();
  for (const m of messages) {
    if (m.role !== 'assistant' || m.payload === null || typeof m.payload !== 'object') {
      continue;
    }
    const content = (m.payload as Record<string, unknown>).content;
    if (!Array.isArray(content)) {
      continue;
    }
    for (const block of content) {
      if (block === null || typeof block !== 'object') {
        continue;
      }
      const b = block as Record<string, unknown>;
      if (b.type === 'toolCall' && typeof b.id === 'string') {
        index.set(b.id, b.arguments);
      }
    }
  }
  return index;
};

/**
 * Render a tool call's `input` object as `(key: value, key: value, ...)`
 * for the toolResult card's header preview. Values are rendered as a
 * short scalar form so the line stays scannable; the full structure is
 * still in the JSON body on expand. Returns `''` for an empty / missing
 * input so the caller can fall through to the no-preview case.
 */
const formatToolParams = (input: unknown): string => {
  if (input === null || typeof input !== 'object') {
    return '';
  }
  const entries = Object.entries(input as Record<string, unknown>);
  if (entries.length === 0) {
    return '';
  }
  const parts = entries.map(([k, v]) => `${k}: ${formatParamValue(v)}`);
  return `(${parts.join(', ')})`;
};

const MAX_PARAM_STRING_LEN = 40;
const formatParamValue = (v: unknown): string => {
  if (v === null) {
    return 'null';
  }
  if (typeof v === 'string') {
    return v.length > MAX_PARAM_STRING_LEN ? `"${v.slice(0, MAX_PARAM_STRING_LEN)}…"` : `"${v}"`;
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  if (Array.isArray(v)) {
    return `[${v.length}]`;
  }
  if (typeof v === 'object') {
    return `{${Object.keys(v as Record<string, unknown>).length}}`;
  }
  return typeof v;
};

/**
 * Extract a `toolResult` row's `toolCallId` from its GraphQL JSON payload.
 * Defensive — a producer drift that drops the field shouldn't crash the
 * renderer.
 */
const readToolCallId = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const id = (payload as Record<string, unknown>).toolCallId;
  return typeof id === 'string' ? id : null;
};

/**
 * Narrow the `toolResult` payload shape from the `unknown` GraphQL JSON
 * scalar so the collapsed header can surface `toolName` + `isError`
 * without rendering the (often noisy) `details` body. Defensive on every
 * field — a future producer drift shouldn't crash the renderer.
 */
const readToolResultPayload = (payload: unknown): { toolName: string | null; isError: boolean } => {
  if (payload === null || typeof payload !== 'object') {
    return { toolName: null, isError: false };
  }
  const obj = payload as Record<string, unknown>;
  const toolName = typeof obj.toolName === 'string' ? obj.toolName : null;
  const isError = obj.isError === true;
  return { toolName, isError };
};

/**
 * Pull the `kind` discriminator out of a `system_observation` or
 * `system_instruction` payload (`'attachment'`, `'session_bootstrap'`,
 * etc.). Both payload families use the same `kind` field and the enum
 * is intentionally open to grow — keep the read defensive so a newly
 * added kind that mops-web hasn't been rebuilt against still renders
 * cleanly.
 */
const readPayloadKind = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const kind = (payload as Record<string, unknown>).kind;
  return typeof kind === 'string' ? kind : null;
};

/**
 * Pull every `{ type: 'text', text: string }` block out of a user or
 * assistant message's `content` array. The Anthropic SDK message shape
 * interleaves `text` and `tool_use` (or, on user rows, `tool_result`)
 * blocks; the prose is what an operator scanning a transcript actually
 * wants to read, so we lift it above the collapsed JSON. Defensive —
 * non-array content (legacy plain-string payloads, or future shape drift)
 * falls back gracefully.
 */
const extractTextBlocks = (payload: unknown): string[] => {
  if (payload === null || typeof payload !== 'object') {
    return [];
  }
  const content = (payload as Record<string, unknown>).content;
  if (typeof content === 'string') {
    return content.trim().length > 0 ? [content] : [];
  }
  if (!Array.isArray(content)) {
    return [];
  }
  const out: string[] = [];
  for (const block of content) {
    if (block !== null && typeof block === 'object') {
      const b = block as Record<string, unknown>;
      if (b.type === 'text' && typeof b.text === 'string' && b.text.trim().length > 0) {
        out.push(b.text);
      }
    }
  }
  return out;
};

interface ICollapsibleMessageCardProps {
  message: MessageRow;
  accent: IRoleAccent;
  metadata: React.ReactNode;
  /** Extra header bits sitting next to the role label (toolName, failed badge, kind discriminator, etc). */
  headerExtras?: React.ReactNode;
  /**
   * Optional one-line preview of the message body, rendered inline
   * after the role label and ellipsized to fit the header row. Lets an
   * operator scan a transcript without expanding every card. Full text
   * is still in the JSON below on expand. Accepts `ReactNode` so
   * callers can render styled placeholders (e.g. italic `Thinking…` on
   * a tool-use-only assistant round).
   */
  headerPreview?: React.ReactNode;
}

/**
 * Shared collapsible card. Five roles use it (toolResult, assistant,
 * user, system_instruction, system_observation) — the JSON payload is
 * hidden by default and expands on click. Header shows the chevron,
 * role label, optional extras, an optional ellipsized preview of the
 * message body, and the right-aligned metadata strip.
 */
const CollapsibleMessageCard = ({
  message,
  accent,
  metadata,
  headerExtras,
  headerPreview,
}: ICollapsibleMessageCardProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const toggle = () => setExpanded((v) => !v);
  return (
    <article className={cx('even:bg-bg-secondary/40 my-1 border-l-2 py-0.5 pl-3 pr-2', accent.border)}>
      <button
        type="button"
        onClick={toggle}
        className="hover:bg-bg-secondary -ml-1 flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] uppercase"
        aria-expanded={expanded}
      >
        <span className="text-fg-quaternary shrink-0">{expanded ? '▾' : '▸'}</span>
        <span className={cx('shrink-0', accent.label)}>{message.role}</span>
        {headerExtras !== undefined && <span className="shrink-0">{headerExtras}</span>}
        {headerPreview !== undefined && (
          <span className={cx('min-w-0 flex-1 truncate normal-case', accent.prose)}>{headerPreview}</span>
        )}
        <span className="text-fg-tertiary ml-auto flex shrink-0 gap-2">{metadata}</span>
      </button>
      {expanded && (
        <div className="whitespace-pre-wrap pl-1 pt-2 font-mono text-[11px] leading-snug">
          <JsonView
            value={message.payload}
            collapseArrayKeys={message.role === 'toolResult' ? COLLAPSED_TOOL_RESULT_KEYS : undefined}
            prominentStringKeys={PROMINENT_CONTENT_KEYS}
          />
        </div>
      )}
      {message.errorMessage && (
        <div className="text-fg-error-primary mt-1 font-mono text-[11px]">errorMessage: {message.errorMessage}</div>
      )}
    </article>
  );
};

const MessageCard = ({ message, turnStartedAtMs, toolCallArgs }: IMessageCardProps) => {
  const accent = ROLE_ACCENT[message.role] ?? DEFAULT_ROLE_ACCENT;
  // Each cell is two stacked fixed-width sub-cells: label
  // (right-aligned, `w-[3ch]`) and value (right-aligned, `w-[7ch]`)
  // with a tight gap between. Labels land at the same screen-X across
  // every row (right edge of the label sub-cell), values' right edges
  // align too, and the inter-pair gap is fixed regardless of value
  // length. Empty cells still hold their width so alignment doesn't
  // collapse when a chip is absent. `normal-case` breaks out of the
  // parent header's `uppercase` so digits stay readable. `modelId` is
  // intentionally dropped from per-row chips — it's the same
  // hardcoded value on every assistant row in a session; the JSON
  // expansion still has it.
  const hasLatency = message.latencyMs !== null && message.latencyMs !== undefined;
  const hasThinking = message.thinkingMs !== null && message.thinkingMs !== undefined;
  const hasTtft = message.ttftMs !== null && message.ttftMs !== undefined;
  const metadata = (
    <span className="flex items-center gap-3 font-mono normal-case tabular-nums">
      <span title="Per-thread sequence: monotonic counter assigned at append time. Stable across rehydration; used as the keyset cursor for incremental loads.">
        {renderMetricChip('seq', String(message.sequence))}
      </span>
      <span title="Delta from turn start: orchestrator wall-clock between this row's startedAt and the first row of the turn. The first row of each turn renders +0ms.">
        {renderMetricChip('Δ', formatTurnDelta(message.startedAt ?? null, turnStartedAtMs))}
      </span>
      <span title="Wall-clock time the model spent inside thinking blocks during this round. Summed across interleaved blocks (Anthropic extended thinking). NULL on tool-result, synthetic, and non-thinking rows.">
        {renderMetricChip(hasThinking ? '🧠' : '', hasThinking ? formatDurationMs(message.thinkingMs ?? null) : '')}
      </span>
      <span title="Time-to-first-token: orchestrator wall-clock from round start to the first user-facing text token. Excludes thinking and tool-call time — the user-perceived latency.">
        {renderMetricChip(hasTtft ? 'ttft' : '', hasTtft ? formatDurationMs(message.ttftMs ?? null) : '')}
      </span>
      <span title="Latency: full wall-clock duration of this round (completedAt − startedAt). For tool-result rows, the tool execution time. Includes thinking + post-text streaming for assistant rounds.">
        {renderMetricChip(
          hasLatency ? 'lat' : '',
          hasLatency ? formatDurationMs(message.latencyMs ?? null) : '',
          'text-fg-primary'
        )}
      </span>
    </span>
  );

  if (message.role === 'toolResult') {
    const toolMeta = readToolResultPayload(message.payload);
    const toolCallId = readToolCallId(message.payload);
    const paramsPreview = toolCallId !== null ? formatToolParams(toolCallArgs.get(toolCallId)) : '';
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        headerPreview={paramsPreview.length > 0 ? paramsPreview : undefined}
        headerExtras={
          <>
            {toolMeta.toolName && <span className="font-bold normal-case text-fuchsia-400">{toolMeta.toolName}</span>}
            {toolMeta.isError && (
              <span className="text-fg-error-primary border-fg-error-primary inline-flex items-center border px-1 py-px text-[9px] uppercase tracking-wider">
                failed
              </span>
            )}
          </>
        }
      />
    );
  }

  if (message.role === 'assistant' || message.role === 'user') {
    const text = extractTextBlocks(message.payload).join(' ');
    // Tool-use-only assistant rounds emit no user-facing text — the
    // model decided to call tools first. Surface a dimmed italic
    // placeholder so the row reads as "no message yet, still working"
    // rather than as an empty line the operator might miss.
    const preview: React.ReactNode =
      text.length > 0 ? (
        text
      ) : message.role === 'assistant' ? (
        <span className="text-fg-tertiary italic">Thinking…</span>
      ) : undefined;
    return <CollapsibleMessageCard message={message} accent={accent} metadata={metadata} headerPreview={preview} />;
  }

  if (message.role === 'system_instruction' || message.role === 'system_observation') {
    const kind = readPayloadKind(message.payload);
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        headerExtras={kind && <span className="text-fg-secondary normal-case">{kind}</span>}
      />
    );
  }

  // Fallthrough — unknown roles render uncollapsed (their shape is
  // unknown, so the JSON IS the signal). Add a dedicated branch above
  // when a new role needs custom rendering.
  return (
    <article className={cx('even:bg-bg-secondary/40 my-1 border-l-2 py-0.5 pl-3 pr-2', accent.border)}>
      <header className="text-fg-tertiary flex gap-2 font-mono text-[10px] uppercase">
        <span className={accent.label}>{message.role}</span>
        {metadata}
      </header>
      <div className="whitespace-pre-wrap pl-1 pt-1 font-mono text-[11px] leading-snug">
        <JsonView value={message.payload} prominentStringKeys={PROMINENT_CONTENT_KEYS} />
      </div>
      {message.errorMessage && (
        <div className="text-fg-error-primary mt-1 font-mono text-[11px]">errorMessage: {message.errorMessage}</div>
      )}
    </article>
  );
};

/**
 * Top-level payload keys whose array value renders collapsed by default in the
 * expanded JSON. `content` on a `toolResult` is `[{ type: 'text', text:
 * JSON.stringify(details) }]` — a stringified duplicate of the sibling
 * `details` object (see `jsonResult` in @orn/api), so it's noise an operator
 * almost never wants un-collapsed.
 */
const COLLAPSED_TOOL_RESULT_KEYS: ReadonlySet<string> = new Set(['content']);

/**
 * Message-payload string keys whose value is prose worth bolding in the
 * rendered JSON — the Anthropic SDK message shape carries user / assistant text
 * under `content` / `text` blocks.
 */
const PROMINENT_CONTENT_KEYS: ReadonlySet<string> = new Set(['content', 'text']);

export default AssistantSessionsScene;
